import { ApiEnvelope, isApiEnvelope } from './migrations';
import { ApiError, getJson, getJsonWithStatus, postJson, postJsonWithStatus } from './client';

// --- Tenants registry API (FD-155 / FD-156, ADR-023) --------------------------
// The tenant-management console reads the customer-tenant Tenants registry — the
// source-of-truth for every Entra tenant a deployment has worked with. All
// endpoints are admin reads/writes behind EasyAuth (ADR-014) and return the
// canonical ADR-007 envelope (payload under `data`, camelCase keys).
//
// Contract (docs/TASKS.md API Contract Changes, FD-155/FD-156 rows):
//   GET  /v1/tenants[?role=source|destination]  → data.tenants[]  (TenantSummary)
//   GET  /v1/tenants/{tenantId}                  → data.tenant     (TenantDetail)
//   POST /v1/tenants/{tenantId}/refresh          → data.tenant     (freshly refreshed)
//   PATCH /v1/tenants/{tenantId}/service-account → data            (FD-439, FD-ADR-042)
//   PATCH /v1/tenants/{tenantId}/application     → data            (FD-524, FD-ADR-046)
//   404 TENANT_NOT_FOUND when a tenant is absent from the registry.
//
// The registry stores identifiers, structural links, and refreshed verdict
// snapshots — never secrets, certificates, Key Vault contents, BPRT token
// values, or recovery passwords (ADR-023 §2). Keep these interfaces aligned
// with the FD-155/FD-156 contract rows (frontend/CLAUDE.md data-sync rule).

/**
 * Refreshed consent verdict for a tenant (ADR-023 §1). Three distinct states —
 * the UI MUST render them differently:
 *  - `consented`   — verified, all required Graph roles present.
 *  - `insufficient`— verified, some required roles missing → real action needed.
 *  - `unknown`     — the consent/Graph read FAILED → "couldn't verify" (NOT the
 *                    same as insufficient; never collapse the two).
 */
export type ConsentStatus = 'consented' | 'insufficient' | 'unknown';

/** One row of GET /v1/tenants — the cross-migration tenant list (FD-155). */
export interface TenantSummary {
  /** Entra tenant GUID (lowercased server-side), the registry RowKey. */
  tenantId: string;
  /** Best-effort domain label; `null` when never resolved. */
  tenantDomain: string | null;
  /** Best-effort org display name; `null` when unavailable. */
  displayName: string | null;
  /** Multi-valued: a tenant may be `source` in one migration and `destination` in another. */
  roles: string[];
  /** Refreshed consent snapshot verdict. */
  consentStatus: ConsentStatus;
  /** When the snapshot was last refreshed; drives the "checked at" stamp. `null` if never. */
  refreshedUtc: string | null;
  /** Count of migrations this tenant is linked to. */
  linkedMigrationCount: number;
}

/**
 * The ADR-020 tenant-app enterprise application (service principal) as it exists
 * in this tenant. Full identifiers are surfaced to authenticated admins (ADR-023
 * §5) — configuration identifiers, not secrets.
 */
export interface ServicePrincipalView {
  objectId: string;
  appId: string;
  displayName: string;
  /** Granted Graph app-roles on this service principal. */
  appRoles: string[];
}

/**
 * The ADR-021 BPRT minting service account (destination tenants only). Records
 * the account's UPN, assigned role, presence, and CA-exclusion health — NEVER
 * its certificate or any credential (ADR-023 §2). `null` when the SA is not yet
 * configured (a pilot/runbook artifact — show "not configured", not an error).
 */
export interface BprtServiceAccountView {
  upn: string;
  role: string;
  present: boolean;
  /** `true` healthy, `false` not excluded, `null` undeterminable ("unknown"). */
  caExclusionHealthy: boolean | null;
}

/** Structural link from a tenant to a migration it participates in (ADR-023 §1). */
export interface LinkedMigrationView {
  migrationId: string;
  migrationWave: string;
  /** This tenant's role in that migration (`source` | `destination`). */
  role: string;
}

/** Per-linked-wave BPRT status projected from Migrations rows (ADR-023 §1). */
export interface WaveBprtStatusView {
  migrationId: string;
  migrationWave: string;
  /** BPRT lifecycle state (e.g. NotActivated / Active / Expired / Revoked). */
  bprtStatus: string;
  /** BPRT expiry; `null` when the wave was never activated. */
  bprtExpiresUtc: string | null;
}

/**
 * The tenant application's certificate state, derived at read time by the server
 * (FD-529, `FD-ADR-046` §6). Stored nowhere — recomputing it from the row on every
 * read is what keeps it from going stale in the way a persisted verdict would.
 */
export interface TenantAppCertificateView {
  /**
   * FOUR values, and COLLAPSING ANY TWO IS THE DEFECT THIS FIELD EXISTS TO PREVENT
   * — the `TenantServiceAccountsPage` discipline, applied to a credential:
   *
   *  - `not-configured` — no application on file for this tenant at all.
   *  - `unknown`        — an application IS on file and its expiry is not known.
   *                       The ORDINARY state for one recorded through `PATCH`, and
   *                       for an unparseable value. **Never render this as healthy.**
   *                       "We cannot tell" must not read as "fine" for a credential;
   *                       that is the FD-ADR-027 fail-closed direction.
   *  - `due`            — inside the warning window, OR already past. Already-expired
   *                       is deliberately not a fifth value: the operator action is
   *                       identical, and an extra state would split one queue in two.
   *  - `healthy`        — expiry known and comfortably ahead.
   *
   * A TRANSIENT probe failure is deliberately none of these. It is a read that did
   * not complete, not a fact about the tenant — the query's own error state carries
   * it, and rendering it here would make a momentary outage look durable.
   */
  state: 'not-configured' | 'unknown' | 'due' | 'healthy';
  /** ISO-8601 UTC; `null` whenever `state` is `not-configured` or `unknown`. */
  expiresUtc: string | null;
  /** Whole days until expiry — NEGATIVE when already past. `null` when unknown. */
  daysRemaining: number | null;
  /**
   * Outcome of the LAST rotation attempt, or `null` if none has been made.
   *
   * Rides ALONGSIDE `state` rather than instead of it, because they answer different
   * questions — *is this credential running out* and *did the last attempt to renew
   * it work*. A tenant can be healthy-and-rotation-failed (the append never happened;
   * the old key is fine for now) or due-and-rotation-failed, which is the urgent one.
   */
  rotationStatus: string | null;
  rotationUtc: string | null;
  /** Server-supplied failure detail for a failed rotation; `null` otherwise. */
  rotationError: string | null;
  /** The superseded key credential still attached, awaiting removal; `null` if none. */
  previousKeyId: string | null;
  /** `true` exactly when `previousKeyId` is set. */
  removalPending: boolean;
}

/** Unwrapped `data.tenant` payload of GET/POST /v1/tenants/{tenantId} (FD-156). */
export interface TenantDetail {
  tenantId: string;
  tenantDomain: string | null;
  displayName: string | null;
  roles: string[];
  consentStatus: ConsentStatus;
  /** Graph app-roles still needed; `[]` when consented. */
  missingRoles: string[];
  /** Admin-consent URL; `null` when unavailable (e.g. unknown/undeterminable). */
  consentUrl: string | null;
  /**
   * R-FD-055: the console-returning grant link, or `null`/absent for an application
   * created without a console reply URL (then `consentUrl` is the way). Built
   * server-side; the console only appends `state`.
   */
  grantUrl?: string | null;
  servicePrincipals: ServicePrincipalView[];
  /**
   * The hourly Graph probe's REPORT on the minting account — is it there, does it
   * hold the role, is its CA exclusion healthy. Derived. `null` when the probe has
   * never had a UPN to ask about.
   */
  bprtServiceAccount: BprtServiceAccountView | null;
  /**
   * The operator's STATEMENT of which account mints for this destination tenant
   * (FD-ADR-042 §1). Deliberately distinct from `bprtServiceAccount` above — an
   * operator's decision and a probe result must never overwrite each other.
   *
   * `null` means "no per-tenant value", and the server NEVER substitutes the
   * deployment-wide `BPRT_SERVICE_ACCOUNT_UPN` default here. That is what lets the
   * console tell *using the deployment default* apart from *no value anywhere* —
   * two states that demand opposite operator actions, and the one thing a single
   * "not configured" string would destroy.
   */
  bprtServiceAccountUpn: string | null;
  /** EasyAuth principal that last set the UPN; `null` when never set. */
  bprtServiceAccountUpnSetBy: string | null;
  /** When it was last set; `null` when never set. */
  bprtServiceAccountUpnSetUtc: string | null;
  /**
   * The FD-ADR-020 tenant application this tenant uses (`FD-ADR-046` §1) — the
   * Entra application id AirBridge authenticates as when it performs Graph
   * operations in this tenant.
   *
   * `null` means "no per-tenant value", and the server NEVER substitutes the
   * deployment-wide `TENANT_APP_CLIENT_ID` here — exactly as it refuses to
   * substitute the minting-account default above, and for the same reason. It is
   * what lets the console tell *using the deployment-wide application* apart from
   * *no application anywhere*: two states demanding opposite operator actions,
   * which one "not configured" string would destroy. On a Rubix-hosted deployment
   * those states differ sharply — the deployment-wide application belongs to
   * Rubix, not to the customer.
   */
  tenantAppClientId: string | null;
  /**
   * Name of the Key Vault certificate that authenticates that application.
   *
   * A Key Vault object NAME, never certificate material — the certificate itself
   * stays in the vault and no API surface returns it (ADR-023 §2). It is a
   * configuration identifier of the same class as the service-principal object ids
   * already on this view (ADR-023 §5).
   *
   * Always `null` or set TOGETHER with `tenantAppClientId`: the server refuses a
   * write carrying only one, because half an application would authenticate the
   * customer's app with the deployment's certificate name.
   */
  tenantAppCertificateName: string | null;
  /**
   * The application's Entra OBJECT id — a different identifier from `tenantAppClientId`
   * and not interchangeable with it. `PATCH /applications/{id}` takes this one, so
   * rotation refuses without it (`409 TENANT_APP_OBJECT_ID_UNKNOWN`) rather than
   * addressing Graph with a client id and receiving an opaque 404 that reads as
   * "the application is gone".
   *
   * `null` is ordinary, not a fault: an operator recording an application they already
   * hold may not have it, and the write does not demand it.
   */
  tenantAppObjectId: string | null;
  /** EasyAuth principal that last recorded the application; `null` when never set. */
  tenantAppSetBy: string | null;
  /** When it was last recorded; `null` when never set. */
  tenantAppSetUtc: string | null;
  /** Certificate state, derived server-side at read time. Always present. */
  tenantAppCertificate: TenantAppCertificateView;
  linkedMigrations: LinkedMigrationView[];
  waveBprtStatus: WaveBprtStatusView[];
  refreshedUtc: string | null;
  consentCheckedUtc: string | null;
  firstSeenUtc: string | null;
  lastSeenUtc: string | null;
}

/** Unwrapped `data` payload of GET /v1/tenants. */
export interface TenantsListResponse {
  tenants: TenantSummary[];
}

/** Unwrapped `data` payload of GET/POST /v1/tenants/{tenantId}. */
export interface TenantDetailResponse {
  tenant: TenantDetail;
}

/** The tenant-role filter accepted by GET /v1/tenants (`?role=`). */
export type TenantRoleFilter = 'source' | 'destination';

export async function getTenants(role?: TenantRoleFilter): Promise<TenantSummary[]> {
  const path = role ? `/v1/tenants?role=${encodeURIComponent(role)}` : '/v1/tenants';
  const envelope = await getJson<ApiEnvelope<TenantsListResponse>>(path);
  return envelope.data.tenants;
}

export async function getTenant(tenantId: string): Promise<TenantDetail> {
  const envelope = await getJson<ApiEnvelope<TenantDetailResponse>>(
    `/v1/tenants/${encodeURIComponent(tenantId)}`,
  );
  return envelope.data.tenant;
}

/** A role an operator gives a tenant when connecting it (R-FD-055). */
export type TenantRole = 'source' | 'destination';

/** Request body of POST /v1/tenants (FD-563). Exactly one of domain / tenantId. */
export interface RegisterTenantInput {
  domain?: string;
  tenantId?: string;
  /** At least one. Additive on the server: a role is never removed. */
  roles: TenantRole[];
}

/** Unwrapped `data` payload of POST /v1/tenants. */
export interface RegisterTenantResult {
  tenantId: string;
  tenant: TenantDetail;
  /** `false` when the tenant was already registered — the call is idempotent. */
  created: boolean;
  registeredBy: string;
  /**
   * Whether the follow-on registry probe succeeded. `false` WITH A 2xx IS A
   * SUCCESSFUL REGISTRATION and must not be retried.
   */
  refreshed: boolean;
}

/**
 * Connects a tenant before any campaign names it (R-FD-055, FD-ADR-023 §6 as
 * amended). Names and classifies the tenant; provisions nothing in it.
 *
 * Errors: 400 `TENANT_REQUIRED` / `INVALID_TENANT_ID` / `INVALID_DOMAIN` /
 * `ROLES_REQUIRED` / `INVALID_ROLE` / `PRINCIPAL_UNRESOLVED`; 404
 * `TENANT_NOT_FOUND_FOR_DOMAIN`; 409 `TENANT_DOMAIN_MISMATCH`; 502 `DISCOVERY_ERROR`.
 */
export async function registerTenant(input: RegisterTenantInput): Promise<RegisterTenantResult> {
  const envelope = await postJson<ApiEnvelope<RegisterTenantResult>, RegisterTenantInput>(
    '/v1/tenants',
    input,
  );
  return envelope.data;
}

// On-demand re-check (ADR-023 §3): re-runs the live consent/SP/SA checks for one
// tenant and returns the freshly refreshed detail. Backs the console Re-check
// button.
export async function refreshTenant(tenantId: string): Promise<TenantDetail> {
  const envelope = await postJson<ApiEnvelope<TenantDetailResponse>, undefined>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/refresh`,
    undefined,
  );
  return envelope.data.tenant;
}

/**
 * Unwrapped `data` payload of PATCH /v1/tenants/{tenantId}/service-account
 * (FD-439, `FD-ADR-042` §4).
 */
export interface SetTenantServiceAccountResult {
  tenantId: string;
  /** The full detail view, already carrying the new UPN and a re-probed snapshot. */
  tenant: TenantDetail;
  /** What the column held before this write; `null` when it held nothing. */
  previousUpn: string | null;
  /** The EasyAuth principal the server recorded. */
  setBy: string;
  /**
   * Whether the server's follow-on registry re-probe succeeded.
   *
   * `false` WITH A 200 IS A SUCCESSFUL WRITE. The UPN is durably stored and the
   * request MUST NOT be retried — only the health snapshot beside it is stale.
   * A retry affordance here would re-PATCH a write that already landed.
   */
  refreshed: boolean;
}

/**
 * Records which account mints the wave BPRT for this DESTINATION tenant
 * (`FD-ADR-042` §4). A UPN, never a credential — AirBridge holds no credential
 * for this account and the registry stores none (`FD-ADR-032` §1, ADR-023 §2).
 *
 * There is deliberately no clear: the endpoint sets the account and refuses an
 * empty value, because a blank field is indistinguishable from a client spreading
 * an empty optional, and silently erasing an operator's decision is the worse
 * failure. The console therefore offers no "clear" control either.
 *
 * Errors: 400 `INVALID_TENANT_ID` / `SERVICE_ACCOUNT_UPN_REQUIRED` /
 * `INVALID_SERVICE_ACCOUNT_UPN` / `PRINCIPAL_UNRESOLVED`; 404 `TENANT_NOT_FOUND`;
 * 409 `TENANT_NOT_DESTINATION` (a source tenant mints nothing); 500 storage.
 */
export async function setTenantServiceAccount(
  tenantId: string,
  bprtServiceAccountUpn: string,
): Promise<SetTenantServiceAccountResult> {
  // PATCH composed from `getJsonWithStatus` rather than a new client export:
  // same auth, same ADR-007 envelope parsing, same `ApiError` — exactly what
  // `postJson` wraps, with one verb changed.
  const { body } = await getJsonWithStatus<ApiEnvelope<SetTenantServiceAccountResult>>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/service-account`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bprtServiceAccountUpn }),
    },
  );
  return body.data;
}

/** Unwrapped `data` payload of PATCH /v1/tenants/{tenantId}/application (FD-524). */
export interface SetTenantApplicationResult {
  tenantId: string;
  /** The full detail view, already carrying the new application and a re-probed snapshot. */
  tenant: TenantDetail;
  /** What the column held before this write; `null` when it held nothing. */
  previousClientId: string | null;
  /** The EasyAuth principal the server recorded. */
  setBy: string;
  /**
   * Whether the server's follow-on registry re-probe succeeded.
   *
   * `false` WITH A 200 IS A SUCCESSFUL WRITE — the application is durably stored
   * and the request MUST NOT be retried; only the consent snapshot beside it is
   * stale. A retry affordance here would re-PATCH a write that already landed.
   */
  refreshed: boolean;
}

/**
 * Records the FD-ADR-020 tenant application for one tenant (`FD-ADR-046` §7).
 *
 * This RECORDS an application that already exists — the Self-hosted path, and any
 * tenant provisioned out of band. Creating one in a customer's tenant is a
 * different endpoint with a different privilege story (`FD-ADR-046` §3), kept
 * apart so an operator who already holds a client id is not routed through an
 * interactive Application-Administrator sign-in they do not need.
 *
 * BOTH values or neither. The server refuses a write carrying one, because
 * completing the other from the deployment default would authenticate the
 * customer's application with the deployment's certificate name — an opaque
 * authentication failure rather than the configuration error it is.
 *
 * Identifiers only: a client id and a Key Vault object name. No credential
 * crosses this wire in either direction.
 *
 * Unlike the service-account endpoint there is NO destination-role requirement —
 * the tenant application is used against source tenants too.
 */
export async function setTenantApplication(
  tenantId: string,
  tenantAppClientId: string,
  tenantAppCertificateName: string,
  /**
   * The Entra OBJECT id. Optional on the wire and optional here, because an
   * operator recording an application they already hold may not have it to hand —
   * refusing the whole write over a field only rotation needs would block the
   * Self-hosted path over a capability it may never use.
   *
   * Omitted rather than sent empty when absent, so a client spreading an unset
   * field cannot be read as a request to clear one.
   */
  tenantAppObjectId?: string,
): Promise<SetTenantApplicationResult> {
  const objectId = tenantAppObjectId?.trim();
  const { body } = await getJsonWithStatus<ApiEnvelope<SetTenantApplicationResult>>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/application`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tenantAppClientId,
        tenantAppCertificateName,
        ...(objectId ? { tenantAppObjectId: objectId } : {}),
      }),
    },
  );
  return body.data;
}

// ─── FD-527 / FD-529 — the brokered tenant-application flows ────────────────
//
// Creating and rotating the FD-ADR-020 tenant application both run on a CUSTOMER
// ADMIN SIGN-IN, and that is `FD-ADR-046` D1 rather than a limitation of the
// console. `Application.ReadWrite.OwnedBy` was REFUSED, so AirBridge holds no
// standing authority to touch an application object in a customer's directory —
// which means nothing here happens unattended, and the certificate-expiry signal
// on the tenant view is the control that makes that defensible rather than an
// outage with a delay.
//
// Two calls per flow, mirroring `mint-session` → `activate-wave`: begin a device
// authorization grant, then poll a redeem-and-act endpoint. The Graph access token
// is redeemed and consumed ENTIRELY server-side and never enters the browser
// (`FD-ADR-032` §§2-3, applied to a different resource). What crosses this wire is
// a `deviceCode` — a single-use, ~15-minute GRANT HANDLE, worthless until a human
// completes the paired sign-in.

/** Unwrapped `data` of POST /v1/tenants/{tenantId}/application/session (FD-527). */
export interface TenantAppSession {
  tenantId: string;
  /** The Rubix provisioning public client the sign-in runs against. */
  clientId: string;
  /** Echoed back, so the console renders the flow it actually started. */
  purpose: TenantAppPurpose;
  scope: string;
  /** Entered by the admin at `verificationUrl`. Never logged, never in a URL. */
  userCode: string;
  verificationUrl: string;
  /** The grant handle the redeem call presents. Not a credential. */
  deviceCode: string;
  expiresInSeconds: number;
  intervalSeconds: number;
  /**
   * The ABSOLUTE deadline, server-computed.
   *
   * The countdown and the stop condition both anchor on this rather than on
   * `expiresInSeconds`, for FD-302's reason: the console cannot know how much of the
   * window the round trip consumed, so a relative window drifts longer than the real
   * one and polls past a code that has already died.
   */
  expiresUtc: string;
}

/**
 * The credential a redeem-and-act call presents — a discriminated union mirroring
 * the server's own one-of (FD-551, `Resolve-TenantAppCredential`).
 *
 * A UNION RATHER THAN TWO OPTIONAL FIELDS, AND THAT IS THE POINT. The server
 * refuses a body carrying both with `CREDENTIAL_SOURCE_AMBIGUOUS`, and its own
 * comment names how that happens in practice: *"A TypeScript client that spreads an
 * optional field sends `graphAccessToken: \"\"` alongside a real `deviceCode`."* A
 * union cannot be constructed with both, so this client cannot produce the body the
 * server exists to refuse.
 *
 * The device-code arm carries a ~15-minute GRANT HANDLE. The pasted arm carries a
 * real delegated Graph access token, so it is held in component state for one submit
 * and never placed in a URL, a query string, or storage.
 */
export type TenantAppCredential =
  | { deviceCode: string }
  | { graphAccessToken: string }
  /**
   * The redirect sign-in (R-FD-055 / FD-564). The CODE is redeemed by the Manager
   * with the verifier — the access token never reaches this browser. All three come
   * from one sign-in; the server refuses a partial set.
   */
  | { authorizationCode: string; codeVerifier: string; redirectUri: string };

/**
 * Which flow the sign-in is for. The sign-in itself is identical; EXACTLY ONE
 * PRECONDITION INVERTS — create refuses when an application is already on file,
 * rotate refuses when one is not — which is why this is explicit rather than
 * inferred from registry state that may have changed since the page loaded.
 */
export type TenantAppPurpose = 'create' | 'rotate';

export async function beginTenantAppSession(
  tenantId: string,
  purpose: TenantAppPurpose,
): Promise<TenantAppSession> {
  const { body } = await postJsonWithStatus<ApiEnvelope<TenantAppSession>, { purpose: TenantAppPurpose }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/application/session`,
    { purpose },
  );
  return body.data;
}

/** Unwrapped `data` of POST /v1/tenants/{tenantId}/application — a 201 (FD-527). */
export interface CreateTenantApplicationResult {
  tenantId: string;
  clientId: string;
  objectId: string;
  servicePrincipalObjectId: string;
  certificateName: string;
  certificateThumbprint: string;
  displayName: string;
  /**
   * The Graph permissions the application REQUESTS. A request, never a grant —
   * consent is a separate act by a Privileged Role Administrator, which is the
   * privilege split `FD-ADR-046` §3 is built on.
   */
  permissionNames: string[];
  /** Where that consent is granted. The next step, and the console must say so. */
  consentUrl: string;
  /**
   * R-FD-055: the console-returning grant link (`/authorize` + `.default` +
   * `prompt=consent`), or `null` when the application has no console reply URL.
   * Preferred over `consentUrl` when present.
   */
  grantUrl?: string | null;
  createdBy: string;
  tenant: TenantDetail;
}

export async function createTenantApplication(
  tenantId: string,
  credential: TenantAppCredential,
  /**
   * R-FD-055: this console's origin, so the created application can send "Grant
   * permissions" back here. Implied by the redirect sign-in; opt-in for the other
   * two sources. The server ignores an origin it does not serve.
   */
  consoleOrigin?: string,
): Promise<CreateTenantApplicationResult> {
  const { body } = await postJsonWithStatus<
    ApiEnvelope<CreateTenantApplicationResult>,
    TenantAppCredential & { consoleOrigin?: string }
  >(
    `/v1/tenants/${encodeURIComponent(tenantId)}/application`,
    consoleOrigin ? { ...credential, consoleOrigin } : credential,
  );
  return body.data;
}

/** Unwrapped `data` of the session endpoint with `mode: 'redirect'` (FD-564). */
export interface TenantAppRedirectSession {
  tenantId: string;
  mode: 'redirect';
  purpose: TenantAppPurpose;
  /** Microsoft's sign-in page. The browser navigates here in the same tab. */
  authorizeUrl: string;
  /**
   * The PKCE verifier. A redemption handle, not a credential: worthless without the
   * code a human produces by signing in (FD-ADR-046 §3 as amended). Held in
   * sessionStorage for the length of one round trip and nowhere else.
   */
  codeVerifier: string;
  state: string;
  redirectUri: string;
  scope: string;
}

export async function beginTenantAppRedirect(
  tenantId: string,
  purpose: TenantAppPurpose,
  redirectUri: string,
): Promise<TenantAppRedirectSession> {
  const { body } = await postJsonWithStatus<
    ApiEnvelope<TenantAppRedirectSession>,
    { purpose: TenantAppPurpose; mode: 'redirect'; redirectUri: string }
  >(`/v1/tenants/${encodeURIComponent(tenantId)}/application/session`, {
    purpose,
    mode: 'redirect',
    redirectUri,
  });
  return body.data;
}

/** Unwrapped `data` of POST /v1/tenants/{tenantId}/application/rotation — a 201 (FD-529). */
export interface RotateTenantApplicationResult {
  tenantId: string;
  clientId: string;
  certificateName: string;
  certificateThumbprint: string;
  certificateExpiresUtc: string;
  /**
   * The key credential the new one supersedes; `null` when the application carried
   * none to supersede.
   *
   * ROTATION IS APPEND-ONLY. The old key stays valid and attached, because a token
   * minted seconds before the rotation is still being presented by an in-flight
   * broker action in that tenant — revoking here would break those rather than
   * renew anything.
   */
  previousKeyId: string | null;
  /** `true` when a superseded key is still attached. Removal is a separate act. */
  removalPending: boolean;
  rotatedBy: string;
  tenant: TenantDetail;
}

export async function rotateTenantApplication(
  tenantId: string,
  credential: TenantAppCredential,
): Promise<RotateTenantApplicationResult> {
  const { body } = await postJsonWithStatus<
    ApiEnvelope<RotateTenantApplicationResult>,
    TenantAppCredential
  >(`/v1/tenants/${encodeURIComponent(tenantId)}/application/rotation`, credential);
  return body.data;
}

/** Unwrapped `data` of POST …/application/rotation/previous-key (FD-529). */
export interface RemoveTenantApplicationKeyResult {
  tenantId: string;
  clientId: string;
  removedKeyId: string;
  /** How many credentials remain attached. Never zero — see below. */
  retainedKeyCount: number;
  removedBy: string;
  tenant: TenantDetail;
}

/**
 * The second half of append-then-remove: retires the superseded key once tokens
 * signed by it have aged out.
 *
 * IT IS OPTIONAL, AND THE CONSOLE MUST NOT IMPLY OTHERWISE. An operator who never
 * calls it is not in a broken state — the old key simply expires on its own and the
 * registry reports `removalPending` until then. What removal buys is retiring a key
 * EARLY, which matters when the rotation was prompted by a suspected compromise
 * rather than an approaching expiry.
 *
 * It needs its own sign-in, which is D1's recurring bill rather than an oversight.
 *
 * Two refusals separate a rotation from an outage: removing the LAST credential is
 * refused (`409 TENANT_APP_LAST_KEY_REFUSED` — an application with no key credential
 * cannot be authenticated as by anyone, in a customer's tenant, with no way back that
 * does not involve another sign-in), and a key that is already gone returns
 * `409 TENANT_APP_SUPERSEDED_KEY_ABSENT` AND CLEARS THE POINTER, because that is the
 * outcome the operator wanted and the console must stop offering a removal it can
 * never complete.
 */
export async function removeTenantApplicationPreviousKey(
  tenantId: string,
  credential: TenantAppCredential,
): Promise<RemoveTenantApplicationKeyResult> {
  const { body } = await postJsonWithStatus<
    ApiEnvelope<RemoveTenantApplicationKeyResult>,
    TenantAppCredential
  >(`/v1/tenants/${encodeURIComponent(tenantId)}/application/rotation/previous-key`, credential);
  return body.data;
}

/**
 * Error codes these flows return that the console BRANCHES on. Deliberately not an
 * exhaustive transcription of every code in the contract: a closed set the UI acts
 * upon, with everything else rendered as the server's own message.
 *
 * Copying the full list would invite a switch with a friendly sentence per code,
 * and a sentence the console invents for a refusal it does not understand is worse
 * than the server's — which at least came from the code that refused.
 */
const ACTED_ON_TENANT_APP_ERRORS = new Set([
  'AUTHORIZATION_PENDING',
  'AUTHORIZATION_DECLINED',
  'DEVICE_CODE_EXPIRED',
  'GRAPH_DEVICE_CODE_DISABLED',
  'GRAPH_DEVICE_CODE_NOT_CONFIGURED',
  'TENANT_APP_CREATE_UNSUPPORTED',
  'TENANT_APP_ALREADY_SET',
  'TENANT_APP_NOT_SET',
  'TENANT_APP_OBJECT_ID_UNKNOWN',
  'TENANT_APP_LAST_KEY_REFUSED',
  'TENANT_APP_SUPERSEDED_KEY_ABSENT',
  'AUTHORIZATION_CODE_INVALID',
  'REDIRECT_URI_NOT_ALLOWED',
]);

export type TenantAppErrorCode =
  | 'AUTHORIZATION_PENDING'
  | 'AUTHORIZATION_DECLINED'
  | 'DEVICE_CODE_EXPIRED'
  | 'GRAPH_DEVICE_CODE_DISABLED'
  | 'GRAPH_DEVICE_CODE_NOT_CONFIGURED'
  | 'TENANT_APP_CREATE_UNSUPPORTED'
  | 'TENANT_APP_ALREADY_SET'
  | 'TENANT_APP_NOT_SET'
  | 'TENANT_APP_OBJECT_ID_UNKNOWN'
  | 'TENANT_APP_LAST_KEY_REFUSED'
  | 'TENANT_APP_SUPERSEDED_KEY_ABSENT'
  | 'AUTHORIZATION_CODE_INVALID'
  | 'REDIRECT_URI_NOT_ALLOWED'
  | 'UNKNOWN';

export function tenantAppErrorCode(err: unknown): TenantAppErrorCode {
  if (!(err instanceof ApiError)) return 'UNKNOWN';
  const code = isApiEnvelope(err.body) ? err.body.error?.code : undefined;
  return typeof code === 'string' && ACTED_ON_TENANT_APP_ERRORS.has(code)
    ? (code as TenantAppErrorCode)
    : 'UNKNOWN';
}

/**
 * The server's suggested delay before the next poll, in seconds, off the 409
 * envelope's `data`. `undefined` when absent — the caller keeps its own interval
 * rather than inventing one.
 */
export function tenantAppRetryAfterSeconds(err: unknown): number | undefined {
  if (!(err instanceof ApiError) || !isApiEnvelope<{ retryAfterSeconds?: unknown }>(err.body)) {
    return undefined;
  }
  const value = err.body.data?.retryAfterSeconds;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

// True when a thrown error is a 404 TENANT_NOT_FOUND — a tenant absent from the
// registry. The create-migration readiness panel (FD-159) treats this as "new
// tenant, not yet registered", not an error.
export function isTenantNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}
